import React, { useCallback, useContext, useEffect, useState } from 'react';
import styles from './styles.module.css';
import { Button, Tooltip, Grid, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Info from 'components/utils/Alert/Info';
import Error from 'components/utils/Alert/Error';
import Success from 'components/utils/Alert/Success';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import AlertHelper from 'helpers/AlertHelper';
import { ShopContext } from 'Context/ShopContext';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import { UserContext } from 'Context/UserContext';
import { USER_PERMISSIONS } from 'const';
import TableEditDialog from 'components/popups/EditTableDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { InventoryContext } from 'Context/InventoryContext';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DataTable from 'components/utils/DataTable';
import ShopsHelper from 'helpers/ShopsHelper';
import TableFormDialog from 'components/popups/TableFormDialog';
import TableService from 'services/TableService';

function Tables() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { getPriceCategories } = useContext(InventoryContext);

  const [tables, setTables] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [successMsg, setSuccessMsg] = useState();
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [tablesFiltered, setTablesFiltered] = useState(null);
  const [areas, setAreas] = useState(null);

  const userHasSalesPersonWritePermission = user?.shop_permissions?.sales_persons_permission === USER_PERMISSIONS.WRITE;
  const hasEliteAccess = ShopsHelper.hasEliteAccess(shop);

  const handleDeleteClick = (tables) => setSelectedForDelete(tables);
  const handleAddClick = () => setShowAddForm(true);
  const handleEditClick = (tables) => setSelectedForEdit(tables);

  const handleDeleteTable = async () => {
    setLoading(true);
    try {
      await TableService.deleteTables(selectedForDelete.id);
      setTables(tables.filter((table) => table.id !== selectedForDelete.id));
      AlertHelper.setShortMessage(setSuccessMsg, tManagement('Table deleted successfully'));
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    }
    setLoading(false);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filterResponse = tables
      ? tables.filter(
          (table) => table.name.toLowerCase().includes(value.toLowerCase()) || table.id.toString().includes(value)
        )
      : [];
    setTablesFiltered(filterResponse);
  };

  const getTables = useCallback(async () => {
    setLoading(true);
    try {
      const table = await TableService.getTables();
      setTables(table);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
    setLoading(false);
  }, []);

  const getAreas = useCallback(async () => {
    try {
      const areaList = await TableService.getAreas();
      setAreas(areaList || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            {userHasSalesPersonWritePermission && (
              <>
                <Tooltip title={tCommon('Edit Table')}>
                  <IconButton onClick={() => handleEditClick(item)}>
                    <EditIcon className={styles.iconButton} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={tCommon('Delete Table')}>
                  <IconButton onClick={() => handleDeleteClick(item)}>
                    <DeleteIcon className={styles.iconButton} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Grid>
        );
      },
    },
  ];

  const featureIsExpired = () => {
    const currentDate = new Date();
    const validityDate = new Date(shop.kot_validity);
    return shop.kot_validity == null || currentDate > validityDate;
  };

  useEffect(() => {
    if (!shop?.id || featureIsExpired()) {
      return;
    }
    getTables();
    getAreas();
    //eslint-disable-next-line
  }, [getTables, getAreas, shop?.id, shop?.kot_validity]);

  useEffect(() => {
    handleSearch(searchText);
    //eslint-disable-next-line
  }, [tables]);

  useEffect(() => {
    if (ShopsHelper.hasProAccess(shop)) {
      getPriceCategories();
    }
  }, [getPriceCategories, shop]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Tables')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      {shop && featureIsExpired() ? (
        <RestrictionInfo
          title={tManagement('KOT Feature not available')}
          content={tManagement('To get KOT support options, contact support team.')}
        />
      ) : !hasEliteAccess ? (
        <RestrictionInfo
          title={tManagement('Table Management is available on Elite Plan')}
          content={tManagement('To get Table Management options, contact support team.')}
        />
      ) : (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <h2 className={styles.subTitle}>{tCommon('Tables')}</h2>

              <div className={styles.filerInputSec}>
                {userHasSalesPersonWritePermission && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ backgroundColor: '#ff851b' }}
                    className={styles.actionBtn}
                    onClick={handleAddClick}
                  >
                    <NoteAddIcon className={styles.actionBtnIcon} />
                    {tCommon('New Table')}
                  </Button>
                )}
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      handleSearch(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tCommon('Search items')}
                  />
                  <Tooltip title={tCommon('Search')} placement="bottom">
                    <IconButton>
                      <SearchIcon className={styles.searchIcon} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          {errorMsg && (
            <div className={styles.marginTop}>
              <Error title={errorMsg} />
            </div>
          )}
          {successMsg && (
            <div className={styles.marginTop}>
              <Success title={successMsg} />
            </div>
          )}
          {hasEliteAccess && shop.kot_validity !== null && (isLoading || (tables && tables.length > 0)) ? (
            <DataTable
              columns={headerData}
              rows={tablesFiltered ? tablesFiltered : tables || []}
              isLoading={isLoading}
            />
          ) : (
            !isLoading && (
              <Info
                title={tManagement('No tables to list')}
                content={tManagement(
                  'You have no table to list with current filter configuration. Please clear the filters or create a new table'
                )}
              />
            )
          )}
          {showAddForm && (
            <TableFormDialog
              handleClose={() => setShowAddForm(false)}
              handleSuccess={async () => {
                setShowAddForm(false);
                await getTables();
              }}
              areas={areas}
              setErrorMsg={setErrorMsg}
              setSuccessMsg={setSuccessMsg}
            />
          )}
          {selectedForDelete && (
            <ConfirmPopup
              data={selectedForDelete}
              handleClose={() => setSelectedForDelete(null)}
              handleOkay={handleDeleteTable}
              alertTitle={tCommon('Confirm Delete')}
              successBtnName={tCommon('Delete')}
              alertContent={tManagement("Deleted table can't be restored, do you want to delete the table ? ")}
            />
          )}
          {selectedForEdit && (
            <TableEditDialog
              table={selectedForEdit}
              areas={areas}
              handleClose={() => {
                setSelectedForEdit(null);
              }}
              handleSuccess={async () => {
                setSelectedForEdit(null);
                await getTables();
              }}
              setErrorMsg={setErrorMsg}
              setSuccessMsg={setSuccessMsg}
            />
          )}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(Tables);
